import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
    Button,
    InputAdornment,
    Popover,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {useState} from "react";
import {
    durationPartsToSeconds,
    durationSecondsToParts,
    formatDurationDaysHours,
    formatDurationInput
} from "../../Campaign/CampaignDuration";

const FIELDS = [
    {name: "days", label: "Days"},
    {name: "hours", label: "Hours", max: 23},
    {name: "minutes", label: "Minutes", max: 59},
    {name: "seconds", label: "Seconds", max: 59}
];

const normalizePart = (value, max) => {
    if (value === "") {
        return "";
    }

    const numericValue = Math.max(0, Math.floor(Number(value) || 0));
    return max == null ? numericValue : Math.min(numericValue, max);
};

const DEFAULT_UNITS = FIELDS.map(field => field.name);

export default function DurationPickerField({
    value,
    onChange,
    error,
    helperText,
    label = "Recommended Duration",
    units = DEFAULT_UNITS
}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [parts, setParts] = useState(durationSecondsToParts(value));
    const open = Boolean(anchorEl);
    const visibleFields = FIELDS.filter(field => units.includes(field.name));
    const unitHint = visibleFields.map(field => field.label.toLowerCase()).join(", ");
    const formattedValue = units.includes("minutes") || units.includes("seconds")
        ? formatDurationInput(value)
        : value == null ? "" : formatDurationDaysHours(value);

    const handleOpen = event => {
        setParts(durationSecondsToParts(value));
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    const handlePartChange = (name, max) => event => {
        setParts(current => ({
            ...current,
            [name]: normalizePart(event.target.value, max)
        }));
    };

    const handleClear = () => {
        onChange(null);
        handleClose();
    };

    const handleApply = () => {
        onChange(durationPartsToSeconds(parts));
        handleClose();
    };

    return (
        <>
            <TextField
                fullWidth
                label={label}
                value={formattedValue}
                placeholder="Not set"
                error={error}
                helperText={helperText || `Click to set ${unitHint}`}
                onClick={handleOpen}
                onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        handleOpen(event);
                    }
                }}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <AccessTimeIcon color="action" />
                        </InputAdornment>
                    )
                }}
                inputProps={{
                    "aria-haspopup": "dialog",
                    "aria-expanded": open
                }}
                sx={{cursor: "pointer", "& input": {cursor: "pointer"}}}
            />
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                transformOrigin={{vertical: "top", horizontal: "left"}}
            >
                <Stack spacing={2} sx={{p: 2, minWidth: Math.max(230, visibleFields.length * 104 + 22)}}>
                    <Typography variant="subtitle2">{label}</Typography>
                    <Stack direction="row" spacing={1}>
                        {visibleFields.map(field => (
                            <TextField
                                key={field.name}
                                type="number"
                                label={field.label}
                                value={parts[field.name]}
                                onChange={handlePartChange(field.name, field.max)}
                                inputProps={{min: 0, max: field.max, step: 1}}
                                sx={{width: 96}}
                            />
                        ))}
                    </Stack>
                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                        <Button onClick={handleClear}>Clear</Button>
                        <Button variant="contained" onClick={handleApply}>Apply</Button>
                    </Stack>
                </Stack>
            </Popover>
        </>
    );
}
